/**
 * DynamicIntakeForm - Schema-driven intake form
 *
 * Replaces the hardcoded ExtractedFieldsComponent with a dynamic
 * form that renders based on the intake form schema.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  VirtualizedFormSelect,
  Alert,
} from '@roger/ui-components';
import { Loader2, AlertCircle, Check } from 'lucide-react';
import MarkCompletedButton from './MarkCompletedButton';
import { type IntakeResponse } from '../../services/api';
import { useIntake } from '../../hooks/useIntake';
import { IntakeSectionRenderer } from './IntakeSectionRenderer';
import { runValidation } from './validators';
import { applyFormatter } from './formatters';
import {
  useIntakeFormSchema,
  getSortedSections,
  getRequiredFields,
  type IntakeFieldDefinition,
} from '../../config/intakeFormSchema';
import { useStore } from '../../store';

// =============================================================================
// Types
// =============================================================================

export interface DynamicIntakeFormProps {
  intake: IntakeResponse;
  intakeId: string;
  formData: Partial<IntakeResponse>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IntakeResponse>>>;
  onConfirm: () => void | Promise<void>;
  onIntakeUpdate: () => void;
  agencyInsuranceOptions: Array<{ value: string; label: string }>;
  agencyInsurancesLoading: boolean;
  agencyInsurancesError: string | null;
  agencyClinicOptions: Array<{ value: string; label: string }>;
  agencyClinicsLoading: boolean;
  agencyClinicsError: string | null;
  visibleTabCount: number;
  documentNameDict: Record<string, string>;
}

// =============================================================================
// Component
// =============================================================================

export const DynamicIntakeForm: React.FC<DynamicIntakeFormProps> = ({
  intake,
  intakeId,
  formData,
  setFormData,
  onConfirm,
  onIntakeUpdate: _onIntakeUpdate,
  agencyInsuranceOptions,
  agencyInsurancesLoading,
  agencyInsurancesError,
  agencyClinicOptions,
  agencyClinicsLoading,
  agencyClinicsError,
  visibleTabCount,
  documentNameDict,
}) => {
  // =========================================================================
  // Schema Loading
  // =========================================================================

  const {
    schema,
    isLoading: schemaLoading,
    error: schemaError,
  } = useIntakeFormSchema();

  // Get the intake update function
  const { updateIntakeRecord } = useIntake();

  // Check if user has admin permission
  const showAIExplanations = useStore(state =>
    state.hasPermission('intake:ai-explanations')
  );

  // =========================================================================
  // State
  // =========================================================================

  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [saveTimeoutId, setSaveTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null>
  >({});
  const lastSavedDataRef = React.useRef<Partial<IntakeResponse>>({});

  // Debounce timeouts for text field auto-save
  const debounceTimeoutsRef = React.useRef<Record<string, NodeJS.Timeout>>({});

  // Ref for the clinic select to manually set value
  const clinicSelectRef = React.useRef<HTMLInputElement>(null);

  // Track which intake has been formatted on load
  const formattedIntakeIdRef = React.useRef<string | null>(null);

  // =========================================================================
  // Effects
  // =========================================================================

  // Set clinic select value when formData changes or options load
  useEffect(() => {
    if (clinicSelectRef.current && formData.agency_clinic_id) {
      clinicSelectRef.current.value = formData.agency_clinic_id;
      // Trigger change event to ensure VirtualizedFormSelect syncs
      clinicSelectRef.current.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }, [formData.agency_clinic_id, agencyClinicOptions]);

  // =========================================================================
  // Computed values
  // =========================================================================

  // Get sorted sections for rendering
  const sortedSections = useMemo(() => {
    if (!schema) return [];
    return getSortedSections(schema);
  }, [schema]);

  // Get missing required fields from schema
  const missingFields = useMemo(() => {
    if (!schema) return [];
    const requiredFields = getRequiredFields(schema);
    return requiredFields.filter(field => {
      const value = formData[field.field_id as keyof IntakeResponse];
      return (
        !value ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      );
    });
  }, [formData, schema]);

  const missingFieldsCount = missingFields.length;

  // =========================================================================
  // Effects
  // =========================================================================

  // Validate fields when formData changes
  useEffect(() => {
    const errors: Record<string, string | null> = {};

    // Validate all fields that have validators defined
    for (const [, section] of sortedSections) {
      for (const field of section.content) {
        if (field.validator_fn) {
          const value = formData[field.field_id as keyof IntakeResponse];
          errors[field.field_id] = runValidation(
            field.validator_fn,
            value as string
          );
        }
      }
    }

    setValidationErrors(errors);
  }, [formData, sortedSections]);

  // Apply formatters to loaded data (runs once per intake)
  useEffect(() => {
    // Skip if already formatted this intake
    if (formattedIntakeIdRef.current === intakeId) return;
    // Skip if schema or data not ready
    if (!schema || !formData || Object.keys(formData).length === 0) return;

    const formatted: Partial<IntakeResponse> = { ...formData };
    const changedFields: Record<string, unknown> = {};

    Object.values(schema).forEach(section => {
      section.content.forEach(field => {
        if (field.formatter_fn) {
          const value = formData[field.field_id as keyof IntakeResponse];
          if (typeof value === 'string' && value) {
            const formattedValue = applyFormatter(field.formatter_fn, value);
            if (formattedValue !== value) {
              (formatted as Record<string, unknown>)[field.field_id] =
                formattedValue;
              changedFields[field.field_id] = formattedValue;
            }
          }
        }
      });
    });

    // Mark as formatted for this intake (before setState to avoid re-runs)
    formattedIntakeIdRef.current = intakeId;

    if (Object.keys(changedFields).length > 0) {
      // Defer to next tick to avoid React batching issues
      setTimeout(() => {
        setFormData(formatted);
        // Save formatted fields to backend
        updateIntakeRecord(intakeId, changedFields);
      }, 0);
    }
  }, [schema, formData, intakeId, setFormData, updateIntakeRecord]);

  // Cleanup debounce timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  // =========================================================================
  // Auto-save handler
  // =========================================================================

  const handleAutoSave = useCallback(
    async (fieldName: string, value: unknown) => {
      try {
        setSaveStatus('saving');

        // Use updateIntakeRecord which automatically updates the store
        await updateIntakeRecord(intakeId, { [fieldName]: value });

        setSaveStatus('saved');

        // Clear "saved" status after 2 seconds
        if (saveTimeoutId) clearTimeout(saveTimeoutId);
        const timeoutId = setTimeout(() => setSaveStatus('idle'), 2000);
        setSaveTimeoutId(timeoutId);
      } catch (error) {
        console.error('Failed to save field:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    },
    [intakeId, saveTimeoutId, updateIntakeRecord]
  );

  // =========================================================================
  // Field handlers
  // =========================================================================

  const handleFieldChange = useCallback(
    (fieldId: string, value: unknown, fieldType?: string) => {
      setFormData(prev => ({ ...prev, [fieldId]: value }));

      // If has_transferred_from_another_hha is set to false, clear prior_hha
      if (fieldId === 'has_transferred_from_another_hha' && value !== true) {
        setFormData(prev => ({ ...prev, prior_hha: null }));
        handleAutoSave('prior_hha', null);
      }

      // Boolean and dropdown fields save immediately (no blur event)
      if (fieldType === 'boolean' || fieldType === 'dropdown') {
        handleAutoSave(fieldId, value);
        return;
      }

      // Text and list fields use debounced auto-save
      // This ensures changes are saved even if user closes tab without blurring
      if (fieldType === 'text' || fieldType === 'list') {
        // Clear any existing debounce for this field
        if (debounceTimeoutsRef.current[fieldId]) {
          clearTimeout(debounceTimeoutsRef.current[fieldId]);
        }

        // Set up new debounce (1.5 seconds)
        debounceTimeoutsRef.current[fieldId] = setTimeout(() => {
          handleAutoSave(fieldId, value);
          delete debounceTimeoutsRef.current[fieldId];
        }, 1500);
      }
    },
    [setFormData, handleAutoSave]
  );

  const handleFieldBlur = useCallback(
    (field: IntakeFieldDefinition, newValue?: unknown) => {
      // Cancel any pending debounce for this field (we're saving now on blur)
      if (debounceTimeoutsRef.current[field.field_id]) {
        clearTimeout(debounceTimeoutsRef.current[field.field_id]);
        delete debounceTimeoutsRef.current[field.field_id];
      }

      // Use passed value if provided (for date fields), otherwise read from formData
      const value =
        newValue !== undefined
          ? newValue
          : formData[field.field_id as keyof IntakeResponse];

      // Run validation
      if (field.validator_fn) {
        const error = runValidation(field.validator_fn, value as string);
        setValidationErrors(prev => ({ ...prev, [field.field_id]: error }));
      }

      // Handle list fields - convert comma-separated to array
      if (field.type === 'list') {
        const stringValue =
          typeof value === 'string'
            ? value
            : (value as string[] | undefined)?.join(', ') || '';
        const arrayValue = stringValue
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0);

        setFormData(prev => ({ ...prev, [field.field_id]: arrayValue }));

        // Save array value
        const originalValue = intake[field.field_id as keyof IntakeResponse] as
          | string[]
          | undefined;
        const originalArray = originalValue || [];
        if (JSON.stringify(arrayValue) !== JSON.stringify(originalArray)) {
          handleAutoSave(field.field_id, arrayValue);
        }
        return;
      }

      // Auto-save for date and boolean fields happens on change, not blur
      if (field.type === 'date' || field.type === 'boolean') {
        if (value !== intake[field.field_id as keyof IntakeResponse]) {
          handleAutoSave(field.field_id, value);
        }
        return;
      }

      // Auto-save for dropdown happens on change
      if (field.type === 'dropdown') {
        if (value !== intake[field.field_id as keyof IntakeResponse]) {
          handleAutoSave(field.field_id, value);
        }
        return;
      }

      // Auto-save for text fields on blur
      if (
        value !== intake[field.field_id as keyof IntakeResponse] ||
        lastSavedDataRef.current[field.field_id as keyof IntakeResponse] !==
          value
      ) {
        handleAutoSave(field.field_id, value);
      }
    },
    [formData, intake, handleAutoSave, setFormData]
  );

  // =========================================================================
  // Render
  // =========================================================================

  // Handle schema loading state
  if (schemaLoading) {
    return (
      <Card className='h-full flex flex-col'>
        <CardContent className='flex-1 flex items-center justify-center'>
          <div className='flex items-center gap-2 text-gray-500'>
            <Loader2 className='h-5 w-5 animate-spin' />
            <span>Loading form...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle schema error state
  if (schemaError || !schema) {
    return (
      <Card className='h-full flex flex-col'>
        <CardContent className='flex-1 flex items-center justify-center'>
          <div className='flex items-center gap-2 text-red-600'>
            <AlertCircle className='h-5 w-5' />
            <span>Failed to load form schema</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b pb-3 bg-white'>
        <div className='flex items-center justify-between gap-3 w-full'>
          <div className='flex items-center gap-3'>
            {/* Save Status Indicator */}
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-500'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='flex items-center gap-2 text-xs text-green-600'>
                <Check className='h-3 w-3' />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <div className='flex items-center gap-2 text-xs text-red-600'>
                <AlertCircle className='h-3 w-3' />
                <span>Save failed</span>
              </div>
            )}
          </div>
          <div className='flex items-center gap-3'>
            {/* Action Button - Mark Completed */}
            {visibleTabCount > 1 && (
              <MarkCompletedButton
                intakeId={intakeId}
                tabKey='extraction'
                isComplete={
                  intake.tab_statuses?.extraction === 'complete' ||
                  intake.tab_statuses?.extraction === 'complete_with_errors'
                }
                onStatusChange={onConfirm}
                className='h-7 px-3'
              />
            )}
          </div>
        </div>
        {/* Validation Error Summary */}
        {!formData.agency_clinic_id && agencyClinicOptions.length > 1 && (
          <Alert className='mt-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
            <div className='flex items-center gap-2'>
              <AlertCircle className='h-4 w-4 flex-shrink-0 text-red-600' />
              <span className='font-medium'>
                Clinic selection is required to proceed.
              </span>
            </div>
          </Alert>
        )}

        {/* Missing Fields Warning (Non-blocking) */}
        {missingFieldsCount > 0 && (
          <Alert className='mt-3 py-2 px-3 bg-amber-50 border-amber-100 text-amber-900 text-xs'>
            <div className='flex items-start gap-2'>
              <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600' />
              <div className='flex-1'>
                <p className='font-medium mb-1'>
                  Invalid fields required to push to EMR:
                </p>
                <div className='flex flex-wrap gap-1'>
                  {missingFields.map((field, i) => (
                    <span key={field.field_id}>
                      {field.label}
                      {i < missingFields.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Alert>
        )}
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4'>
        {/* Clinic Selection - Only show if multiple clinics available */}
        {agencyClinicOptions.length > 1 && (
          <div className='mb-4'>
            <VirtualizedFormSelect
              ref={clinicSelectRef}
              label='Clinic'
              placeholder={
                agencyClinicsLoading
                  ? 'Loading clinics...'
                  : agencyClinicsError
                    ? 'Error loading clinics'
                    : 'Select clinic for this patient'
              }
              options={agencyClinicOptions}
              required={true}
              disabled={agencyClinicsLoading}
              error={
                !formData.agency_clinic_id ? 'Clinic is required' : undefined
              }
              name='agency_clinic_id'
              onChange={e => {
                const newValue = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  agency_clinic_id: newValue,
                }));
                // Auto-save immediately when clinic changes
                if (newValue !== intake.agency_clinic_id) {
                  handleAutoSave('agency_clinic_id', newValue);
                }
              }}
              onBlur={() => {
                // Create a mock field definition for the blur handler
                const clinicField: IntakeFieldDefinition = {
                  field_id: 'agency_clinic_id',
                  label: 'Clinic',
                  type: 'dropdown',
                  required: true,
                  dropdown_source: 'agency_clinic',
                };
                handleFieldBlur(clinicField);
              }}
            />
          </div>
        )}

        <div className='space-y-5'>
          {sortedSections.map(([sectionKey, section]) => (
            <IntakeSectionRenderer
              key={sectionKey}
              sectionKey={sectionKey}
              section={section}
              formData={formData}
              validationErrors={validationErrors}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
              showAIExplanations={showAIExplanations}
              agencyInsuranceOptions={agencyInsuranceOptions}
              agencyInsurancesLoading={agencyInsurancesLoading}
              agencyInsurancesError={agencyInsurancesError}
              agencyClinicOptions={agencyClinicOptions}
              agencyClinicsLoading={agencyClinicsLoading}
              agencyClinicsError={agencyClinicsError}
              selectedIntake={intake}
              documentNameDict={documentNameDict}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
